import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { BUSINESS, COMPLIANCE_LINE_SHORT } from "@/lib/constants";

export const Footer = () => {
    return (
        <footer data-testid="site-footer" className="bg-[#1A362B] text-white/90 mt-24">
            <div className="container-prose py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-sm bg-[#C69C6D] text-white grid place-items-center font-serif text-xl">FW</div>
                            <div>
                                <div className="font-serif text-2xl text-white tracking-tight">{BUSINESS.brand}</div>
                                <div className="text-[10px] tracking-[0.25em] uppercase text-white/60">Australia-wide · Online</div>
                            </div>
                        </Link>
                        <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                            A boutique Australia-wide online mortgage brokerage helping families, investors and business
                            owners secure the right finance — with honesty, expertise and care.
                        </p>
                        <p className="mt-6 text-xs text-white/40">{BUSINESS.domain}</p>
                    </div>

                    <div className="md:col-span-3">
                        <div className="overline text-white/70 mb-4">Sitemap</div>
                        <ul className="space-y-3 text-sm">
                            <li><Link to="/" className="hover:text-[#C69C6D] transition" data-testid="footer-link-home">Home</Link></li>
                            <li><Link to="/about" className="hover:text-[#C69C6D] transition" data-testid="footer-link-about">About Gurpreet</Link></li>
                            <li><Link to="/services" className="hover:text-[#C69C6D] transition" data-testid="footer-link-services">Services</Link></li>
                            <li><Link to="/how-it-works" className="hover:text-[#C69C6D] transition" data-testid="footer-link-how-it-works">How It Works</Link></li>
                            <li><Link to="/calculator" className="hover:text-[#C69C6D] transition" data-testid="footer-link-calculator">Repayment Calculator</Link></li>
                            <li><Link to="/blog" className="hover:text-[#C69C6D] transition" data-testid="footer-link-blog">Blog</Link></li>
                            <li><Link to="/contact" className="hover:text-[#C69C6D] transition" data-testid="footer-link-contact">Contact</Link></li>
                            <li className="pt-2 mt-2 border-t border-white/10"></li>
                            <li><Link to="/credit-guide" className="hover:text-[#C69C6D] transition" data-testid="footer-link-credit-guide">Credit Guide &amp; Privacy</Link></li>
                            <li><Link to="/terms" className="hover:text-[#C69C6D] transition" data-testid="footer-link-terms">Terms &amp; Conditions</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <div className="overline text-white/70 mb-4">Get in touch</div>
                        <ul className="space-y-4 text-sm">
                            <li className="flex gap-3">
                                <MapPin className="w-4 h-4 mt-0.5 text-[#C69C6D] shrink-0" />
                                <span className="text-white/80 leading-relaxed">
                                    {BUSINESS.addressLine1}<br />
                                    {BUSINESS.addressLine2}<br />
                                    {BUSINESS.addressLine3}
                                </span>
                            </li>
                            <li className="flex gap-3">
                                <Phone className="w-4 h-4 mt-0.5 text-[#C69C6D] shrink-0" />
                                <a href={`tel:${BUSINESS.phoneTel}`} data-testid="footer-phone" className="hover:text-[#C69C6D] transition">{BUSINESS.phone}</a>
                            </li>
                            <li className="flex gap-3">
                                <Mail className="w-4 h-4 mt-0.5 text-[#C69C6D] shrink-0" />
                                <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email" className="hover:text-[#C69C6D] transition break-all">{BUSINESS.email}</a>
                            </li>
                        </ul>
                        <Link to="/contact" data-testid="footer-cta" className="mt-8 inline-flex items-center gap-2 text-[#C69C6D] hover:text-white transition group">
                            Request a callback
                            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                        </Link>
                    </div>
                </div>

                {/* Compliance / Legal disclosure block */}
                <div className="mt-16 pt-10 border-t border-white/10">
                    <div className="overline text-white/60 mb-4">Compliance &amp; licensing</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 text-xs text-white/60 leading-relaxed">
                        <div data-testid="footer-company">
                            <span className="text-white/80 font-semibold">Company:</span> {BUSINESS.companyName}<br />
                            <span className="text-white/80 font-semibold">ABN:</span> {BUSINESS.companyAbn}
                        </div>
                        <div data-testid="footer-acl">
                            <span className="text-white/80 font-semibold">Australian Credit Licence:</span> {BUSINESS.aclHolder} — ACL No. {BUSINESS.aclNumber}
                        </div>
                        <div data-testid="footer-credit-rep">
                            <span className="text-white/80 font-semibold">Credit Representative:</span> {BUSINESS.creditRepName} — No. {BUSINESS.creditRepNumber}
                        </div>
                        <div data-testid="footer-corporate-rep">
                            <span className="text-white/80 font-semibold">Corporate Credit Representative:</span> {BUSINESS.corporateCreditRepName} — No. {BUSINESS.corporateCreditRepNumber}
                        </div>
                    </div>
                    <p data-testid="footer-compliance-line" className="mt-6 text-xs text-white/55 leading-relaxed max-w-4xl">
                        {COMPLIANCE_LINE_SHORT}
                    </p>
                    <p data-testid="footer-disclaimer" className="mt-4 text-xs text-white/55 leading-relaxed max-w-4xl italic">
                        Disclaimer: {BUSINESS.disclaimer}
                    </p>
                    <a
                        href="https://customer-assets.emergentagent.com/job_wealth-finance-au/artifacts/twohe09v_BRASS%20Credit%20Guide%20and%20Privacy%20v4%20%2838%29.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-credit-guide-pdf"
                        className="mt-5 inline-flex items-center gap-2 text-xs text-[#C69C6D] hover:text-white transition border-b border-[#C69C6D]/40 hover:border-white pb-1"
                    >
                        Download our Credit Guide &amp; Privacy Disclosure (PDF) →
                    </a>
                </div>

                <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-xs text-white/40">
                    <p>© {new Date().getFullYear()} {BUSINESS.companyName}. All rights reserved.</p>
                    <p className="flex flex-wrap gap-x-4">
                        <Link to="/credit-guide" className="hover:text-white/80 transition">Credit Guide &amp; Privacy</Link>
                        <Link to="/terms" className="hover:text-white/80 transition">Terms &amp; Conditions</Link>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
